import type { IntentDefinition } from '../types/intent'

import { DefaultIntents } from './DefaultIntents'

// Dynamic Intent Registry Class
class IntentRegistry {
  private intents: Map<string, IntentDefinition> = new Map()
  private listeners: Set<
    (
      eventType: 'add' | 'update' | 'delete',
      intentName: string,
      intent?: IntentDefinition
    ) => void
  > = new Set()

  // Initialize with default intents
  initializeDefaultIntents(): void {
    // Add all default intents
    Object.values(DefaultIntents).forEach(intent => {
      this.intents.set(intent.name, intent)
    })
  }

  // Add new intent or update existing intent
  registerIntent(intent: IntentDefinition): boolean {
    try {
      this.validateIntent(intent)

      const isUpdate = this.intents.has(intent.name)
      this.intents.set(intent.name, intent)

      // Notify listeners
      this.notifyListeners(isUpdate ? 'update' : 'add', intent.name, intent)

      console.log(
        `Intent '${intent.name}' has been ${
          isUpdate ? 'updated' : 'registered'
        } successfully`
      )
      return true
    } catch (error) {
      console.error(`Error registering intent '${intent.name}':`, error)
      return false
    }
  }

  // Remove intent
  unregisterIntent(intentName: string): boolean {
    if (this.intents.has(intentName)) {
      const intent = this.intents.get(intentName)
      this.intents.delete(intentName)

      // Notify listeners
      this.notifyListeners('delete', intentName, intent)

      console.log(`Intent '${intentName}' has been deleted successfully`)
      return true
    }

    console.warn(`Intent '${intentName}' does not exist`)
    return false
  }

  // Get intent by name
  getIntent(intentName: string): IntentDefinition | undefined {
    return this.intents.get(intentName)
  }

  // Get all intents
  getAllIntents(): Record<string, IntentDefinition> {
    const result: Record<string, IntentDefinition> = {}
    this.intents.forEach((intent, name) => {
      result[name] = intent
    })
    return result
  }

  getAllIntentDescriptions(): string {
    const descriptions: string[] = []
    let intentNumber = 1
    this.intents.forEach((intent, key) => {
      descriptions.push(`${intentNumber}. ${key}`)
      descriptions.push(`   Description: ${intent.description}`)
      descriptions.push(`   Examples: ${intent.examples.join(', ')}`)
      if (intent.entities.length > 0) {
        descriptions.push(`   Entities:`)
        intent.entities.forEach(entity => {
          descriptions.push(
            `     - ${entity.name} (type: ${entity.type}): ${entity.description}`
          )
        })
      }

      if (intent.rules?.length) {
        intent.rules.forEach(rule => {
          descriptions.push(`\n   ${rule.title}:\n`)
          descriptions.push(`${rule.content}\n`)
        })
      }
      intentNumber++
    })
    return descriptions.join('\n')
  }

  // Get list of intent names
  getIntentNames(): string[] {
    return Array.from(this.intents.keys())
  }

  // Check if intent exists
  hasIntent(intentName: string): boolean {
    return this.intents.has(intentName)
  }

  // Search intents by description or examples
  searchIntents(query: string): IntentDefinition[] {
    const lowerQuery = query.toLowerCase()
    const results: IntentDefinition[] = []

    this.intents.forEach(intent => {
      // Search in description
      if (intent.description.toLowerCase().includes(lowerQuery)) {
        results.push(intent)
        return
      }

      // Search in examples
      if (
        intent.examples.some(example =>
          example.toLowerCase().includes(lowerQuery)
        )
      ) {
        results.push(intent)
      }
    })

    return results
  }

  // Register multiple intents at once
  registerMultipleIntents(intents: IntentDefinition[]): {
    success: string[]
    failed: string[]
  } {
    const success: string[] = []
    const failed: string[] = []

    intents.forEach(intent => {
      if (this.registerIntent(intent)) {
        success.push(intent.name)
      } else {
        failed.push(intent.name)
      }
    })

    return { success, failed }
  }

  // Unregister multiple intents at once
  unregisterMultipleIntents(intentNames: string[]): {
    success: string[]
    failed: string[]
  } {
    const success: string[] = []
    const failed: string[] = []

    intentNames.forEach(name => {
      if (this.unregisterIntent(name)) {
        success.push(name)
      } else {
        failed.push(name)
      }
    })

    return { success, failed }
  }

  // Update only part of an intent
  updateIntentPartial(
    intentName: string,
    updates: Partial<IntentDefinition>
  ): boolean {
    const existingIntent = this.intents.get(intentName)
    if (!existingIntent) {
      console.warn(`Intent '${intentName}' does not exist`)
      return false
    }

    const updatedIntent: IntentDefinition = {
      ...existingIntent,
      ...updates,
      name: intentName, // Ensure name is not changed
    }

    return this.registerIntent(updatedIntent)
  }

  // Reset to default state
  resetToDefaults(): void {
    this.intents.clear()
    this.initializeDefaultIntents()
    console.log('Registry has been reset to default state')
  }

  // Export current configuration
  exportConfig(): Record<string, IntentDefinition> {
    return this.getAllIntents()
  }

  // Import configuration from object
  importConfig(config: Record<string, IntentDefinition>): {
    success: string[]
    failed: string[]
  } {
    const intents = Object.values(config)
    return this.registerMultipleIntents(intents)
  }

  // Validate intent before registering
  private validateIntent(intent: IntentDefinition): void {
    if (!intent.name || intent.name.trim() === '') {
      throw new Error('Intent name must not be empty')
    }

    if (!intent.description || intent.description.trim() === '') {
      throw new Error('Intent description must not be empty')
    }

    if (!Array.isArray(intent.examples) || intent.examples.length === 0) {
      throw new Error('Intent must have at least one example')
    }

    if (!Array.isArray(intent.entities)) {
      throw new Error('Entities must be an array')
    }

    if (
      typeof intent.confidence_threshold !== 'number' ||
      intent.confidence_threshold < 0 ||
      intent.confidence_threshold > 1
    ) {
      throw new Error('Confidence threshold must be a number between 0 and 1')
    }

    if (typeof intent.handler !== 'function') {
      throw new Error('Handler must be a function')
    }

    // Validate entities
    intent.entities.forEach((entity, index) => {
      if (!entity.name || entity.name.trim() === '') {
        throw new Error(`Entity ${index + 1} must have a name`)
      }
      if (!entity.type) {
        throw new Error(`Entity '${entity.name}' must have a type`)
      }
      if (typeof entity.required !== 'boolean') {
        throw new Error(
          `Entity '${entity.name}' must have a required property (boolean)`
        )
      }
    })
  }

  // Add listener for changes
  addListener(
    callback: (
      eventType: 'add' | 'update' | 'delete',
      intentName: string,
      intent?: IntentDefinition
    ) => void
  ): void {
    this.listeners.add(callback)
  }

  // Remove listener
  removeListener(
    callback: (
      eventType: 'add' | 'update' | 'delete',
      intentName: string,
      intent?: IntentDefinition
    ) => void
  ): void {
    this.listeners.delete(callback)
  }

  // Notify listeners
  private notifyListeners(
    eventType: 'add' | 'update' | 'delete',
    intentName: string,
    intent?: IntentDefinition
  ): void {
    this.listeners.forEach(callback => {
      try {
        callback(eventType, intentName, intent)
      } catch (error) {
        console.error('Error in listener:', error)
      }
    })
  }

  // Get statistics
  getStats(): {
    totalIntents: number
    totalExamples: number
    totalEntities: number
    avgConfidenceThreshold: number
  } {
    const intents = Array.from(this.intents.values())

    return {
      totalIntents: intents.length,
      totalExamples: intents.reduce(
        (sum, intent) => sum + intent.examples.length,
        0
      ),
      totalEntities: intents.reduce(
        (sum, intent) => sum + intent.entities.length,
        0
      ),
      avgConfidenceThreshold:
        intents.reduce((sum, intent) => sum + intent.confidence_threshold, 0) /
        intents.length,
    }
  }
}

export const intentRegistry = new IntentRegistry()
